/** biome-ignore-all lint/suspicious/noConsole: Allow error logging for toasts */

import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { hasProjectPermission } from "@/app/capabilities/api/check-permission";
import { useGetCapabilities } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import type { ScopedBinding } from "@/app/types";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import type {
  BulkUpsertHooksRequest,
  BulkUpsertHooksResponse,
  CreatePluginRequest,
  PatchPluginRequest,
} from "../types";
import { bulkUpdateHooks } from "./bulk-update-hooks";
import { createPlugin } from "./create-plugin";
import { deletePlugin } from "./delete-plugin";
import { hooksQueryOptions } from "./get-hooks";
import { pluginsQueryOptions } from "./get-plugins";
import { hooksKeys, pluginsKeys } from "./query-keys";
import { updatePlugin } from "./update-plugin";

// =============================================================================
// Plugin Hooks
// =============================================================================

export const useGetPlugins = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...pluginsQueryOptions({ binding, scope }),
  });
};

export const useCreatePlugin = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: CreatePluginRequest) =>
      createPlugin({ client, payload }),
    meta: {
      invalidates: [pluginsKeys.lists({ scope }), hooksKeys.lists({ scope })],
      errorMessage: "Unable to create extension - please try again",
    },
    onSuccess: () => {
      toast.success("Extension created successfully");
    },
  });
};

export const useUpdatePlugin = (
  id: string,
  binding: ScopedBinding = "project"
) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: PatchPluginRequest) =>
      updatePlugin({ client, id, payload }),
    meta: {
      awaits: [pluginsKeys.all({ scope }), hooksKeys.all({ scope })],
      errorMessage: "Unable to update extension",
    },
    onSuccess: () => {
      toast.success("Extension updated successfully");
    },
  });
};

export const useDeletePlugin = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (id: string) => deletePlugin({ client, id }),
    meta: {
      invalidates: [pluginsKeys.lists({ scope }), hooksKeys.lists({ scope })],
      errorMessage: "Unable to delete extension",
    },
    onSuccess: () => {
      toast.success("Extension deleted");
    },
  });
};

// =============================================================================
// Hook Hooks (Active Rules)
// =============================================================================

export const useGetHooks = (
  binding: ScopedBinding = "project",
  hookPoint?: string,
  pluginId?: string
) => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...hooksQueryOptions({ binding, hookPoint, pluginId, scope }),
  });
};

/**
 * Project hooks for the Contextual Access list, gated on `hooks_read`. The list
 * route only guards `plugins_read`, so a caller with `plugins_read` but not
 * `hooks_read` must not fire the hooks query (it would 403); return [] instead.
 * Non-suspense so the page still renders its plugins while hooks are unreadable.
 */
export const useGetProjectHooksIfReadable = () => {
  const scope = useResourceScope();
  const { data: capabilities } = useGetCapabilities();
  const canReadHooks = capabilities
    ? hasProjectPermission(capabilities.authz, PROJECT.hooks.read)
    : false;

  const { data } = useQuery({
    ...hooksQueryOptions({ binding: "project", scope }),
    enabled: canReadHooks,
  });

  return data ?? [];
};

/**
 * Hook to bulk update hooks (priority, status, phase, failure_mode)
 * Use this for reordering hooks or bulk enable/disable operations.
 */
export const useBulkUpdateHooks = () => {
  const scope = useResourceScope();

  return useMutation<
    BulkUpsertHooksResponse,
    Error,
    { payload: BulkUpsertHooksRequest; binding?: ScopedBinding }
  >({
    mutationFn: ({ payload, binding = "project" }) => {
      const client = getEngineApi(scope, binding);
      return bulkUpdateHooks({ client, payload });
    },
    meta: {
      awaits: [pluginsKeys.all({ scope }), hooksKeys.all({ scope })],
      errorMessage: "Unable to update hooks",
    },
    onSuccess: (data) => {
      if (data.failed > 0) {
        toast.warning(
          `Updated ${data.success} hooks, ${data.failed} failed to update`
        );
      } else {
        toast.success(`Successfully updated ${data.success} hooks`);
      }
    },
  });
};
